import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";
import { getHomePathForRoles } from "@/lib/role-routing";

interface OnboardingRouteProps {
  children: React.ReactNode;
}

async function fetchOnboardingStatus(userId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("profiles")
    .select("onboarding_completed")
    .eq("id", userId)
    .single();

  if (error || !data) return false;
  return data.onboarding_completed === true;
}

export function OnboardingRoute({ children }: OnboardingRouteProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [homePath, setHomePath] = useState<"/admin" | "/paciente/minha-saude" | "/app">("/app");

  const { data: onboardingCompleted, isLoading } = useQuery({
    queryKey: ["onboarding-route-status", session?.user?.id ?? "none"],
    queryFn: () => fetchOnboardingStatus(session!.user!.id),
    enabled: !!session?.user?.id,
  });

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => setSession(s));

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      if (!session?.user?.id) {
        setHomePath("/app");
        return;
      }

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id);

      setHomePath(getHomePathForRoles((data ?? []).map((r) => r.role)));
    };

    void loadRoles();
  }, [session?.user?.id]);

  if (isLoading && session?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (session?.user && onboardingCompleted === true) {
    return <Navigate to={homePath} replace />;
  }

  return <>{children}</>;
}
